// Reusable Google AdSense ad slot.
//
// Usage:
//   <AdSlot slot={ADSENSE_SLOT_BANNER} format="auto" />
//   <AdSlot slot={ADSENSE_SLOT_INARTICLE} format="fluid" layout="in-article" />
//
// Renders nothing if ads are disabled (Android app, paid user, missing env).
import { useEffect, useRef } from "react";
import {
  ADSENSE_CLIENT_ID,
  useShouldShowAds,
} from "@/lib/should-show-ads";

interface AdSlotProps {
  slot: string;
  format?: "auto" | "fluid" | "rectangle" | "horizontal" | "vertical";
  layout?: string; // e.g. "in-article"
  responsive?: boolean;
  className?: string;
  /** Min height to reserve space and avoid CLS. */
  minHeight?: number;
}

declare global {
  interface Window {
    adsbygoogle?: unknown[];
  }
}

export const AdSlot = ({
  slot,
  format = "auto",
  layout,
  responsive = true,
  className,
  minHeight = 100,
}: AdSlotProps) => {
  const enabled = useShouldShowAds();
  const pushed = useRef(false);

  useEffect(() => {
    if (!enabled || !slot) return;
    if (pushed.current) return;
    try {
      (window.adsbygoogle = window.adsbygoogle || []).push({});
      pushed.current = true;
    } catch (err) {
      console.warn("[adsense] push failed", err);
    }
  }, [enabled, slot]);

  if (!enabled || !slot) return null;

  return (
    <div className={className} style={{ minHeight }}>
      <ins
        className="adsbygoogle"
        style={{ display: "block", minHeight }}
        data-ad-client={ADSENSE_CLIENT_ID}
        data-ad-slot={slot}
        data-ad-format={format}
        data-ad-layout={layout}
        data-full-width-responsive={responsive ? "true" : "false"}
      />
    </div>
  );
};
